import { ToastNotifyError } from "commonComponents/ToastNotify";
import store from "../redux-store/index";

import ReactGA from "react-ga4";
import { getCurrentPlatform, getCurrentVersionText, isSupportLogin } from "./commonHelpers";
import { getLocalStorage } from "utils";
import { LOCALSTORAGE_KEYS } from "utils/constants";
import { isOffline } from "facade/ServiceFacade";
import { isProdEnv } from "data/env-constants";
import { isSuggestiveSellingEnabled } from "helper/recommenderHelpers";

export function sendPageViewEvent(data: {
  page?: string;
  page_path?: string;
  page_title?: string;
  page_url?: string;
}) {
  const metaInfo = getMetaInfo();
  const payload = {
    ...metaInfo,
    ...data,
  };
  ReactGA.send({ hitType: "pageview", ...payload });
}

export function sendGTMEvent({
  eventName,
  params = {},
}: {
  eventName: string;
  params?: {
    itemId?: string;
    itemPrice?: number;
    itemSource?: string;
    itemName?: string;
    phoneNumber?: string;
    saleId?: string;
    label?: string;
    category?: string;
    loadTime?: number;
    recommendationId?: string;
    recommendedItemsCount?: number;
    catalogItemsCount?: number;
    recommendedItemsTotal?: number;
    catalogItemsTotal?: number;
    billTotal?: number;
    totalItemsCount?: number;
    recommendedUniqueItemsCount?: number;
    catalogUniqueItemsCount?: number;
    wasRecommended?: boolean;
    recommendationsProvider?: string;
    updateType?: string;
    updateStage?: string;
  };
}) {
  const metaInfo = getMetaInfo();
  const dimensionsData = {
    // params
    item_id: params?.itemId,
    item_price: params?.itemPrice,
    item_source: params?.itemSource,
    item_name: params?.itemName,
    phone_number: params?.phoneNumber,
    sale_id: params?.saleId,
    label: params?.label,
    category: params?.category,
    load_time: params?.loadTime,
    recommendation_id: params?.recommendationId,
    recommended_items_count: params?.recommendedItemsCount,
    catalog_items_count: params?.catalogItemsCount,
    recommended_items_total: params?.recommendedItemsTotal,
    catalog_items_total: params?.catalogItemsTotal,
    bill_total: params?.billTotal,
    total_items_count: params?.totalItemsCount,
    recommended_unique_items_count: params?.recommendedUniqueItemsCount,
    catalog_unique_items_count: params?.catalogUniqueItemsCount,
    was_recommended: params?.wasRecommended,
    recommendations_provider: params?.recommendationsProvider,
    update_type: params?.updateType,
    update_stage: params?.updateStage,
  }
  console.log(`[GA4 Event] ${eventName}`, { ...metaInfo, ...dimensionsData });
  ReactGA.event(eventName, {
    ...metaInfo,
    ...dimensionsData,
  });
}

export function getMetaInfo() {
  const {
    userData: { businessUserId, userId },
    businessData: { brandName, branchName, branchId, businessName, accountUUID, branchCode },
  } = store.getState().businessStore;
  const versionText = getCurrentVersionText();

  let internal_user_id;
  if (businessUserId) {
    internal_user_id = userId
      ? businessUserId
      : getLocalStorage(LOCALSTORAGE_KEYS.deviceIdentifier) || businessUserId;
  }
  const recommendationsEnabled = isSuggestiveSellingEnabled(store.getState());
  return {
    internal_user_id,
    account_uuid: accountUUID,
    branch_id: branchId,
    branch_name: branchName,
    branch_code: branchCode,
    brand_name: brandName,
    business_name: businessName,
    device_id: getLocalStorage(LOCALSTORAGE_KEYS.deviceIdentifier) || undefined,
    app_source: getCurrentPlatform(),
    app_version: versionText,
    is_support_login: isSupportLogin() || undefined,
    is_offline_mode: isOffline() || undefined,
    recommendations_enabled: recommendationsEnabled
  };
}

export function sendGTMEventOld({
  category = "UI",
  action,
  label = undefined,
  value = undefined,
  nonInteraction = undefined,
  data = {},
}: {
  category?: string;
  action: string;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
  data?: {
    saleId?: string;
    itemId?: string;
    itemSource?: string;
    itemName?: string;
    itemPrice?: number;
    phoneNumber?: string;
    recommendationId?: string;
    loadTime?: number;
  };
}) {
  const dimensionsData = prepareDimensions();
  ReactGA.event(
    {
      category,
      action,
      label,
      value,
      nonInteraction,
    },
    {
      ...dimensionsData,
      dimension6: data?.saleId,
      dimension7: data?.itemId,
      dimension8: data?.recommendationId,
      dimension9: data?.loadTime,
      dimension10: data?.itemPrice,
      dimension11: data?.itemSource,
      dimension12: data?.itemName,
      dimension13: data?.phoneNumber,
    }
  );
}

export function getGtagId() {
  return process.env.REACT_APP_GTAG_ID;
}

// Moved to @rista/core/helpers/feedbackHelper (index.desktop.ts). The core copy is the same
// function reading through core's own store instead of the app `store` singleton.
//
// IMPORTED as well as re-exported because this file's own body calls it — an `export ... from`
// re-export creates NO local binding (ledger rule 5 / hazard 30).
export { prepareDimensions } from "@rista/core/helpers/feedbackHelper";
import { prepareDimensions } from "@rista/core/helpers/feedbackHelper";

export function initializeGoogleAnalytics() {
  const gtagId = getGtagId();
  if (!gtagId) {
    ToastNotifyError("GTag id not found.");
    return;
  }
  const isProd = true; // isProdEnv
  if (isProd) {
    ReactGA.initialize(gtagId, {
      gtagOptions: { cookie_flags: "SameSite=None;Secure" },
    });
  } else {
    ReactGA.initialize(gtagId, {
      testMode: false,
      gaOptions: { debug_mode: true },
    });
  }
}
